import React, { useState } from "react";
import PropTypes from "prop-types";

import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";

import InputDate from "components/RH/inputs/InputDate";
import EnderecoFuncionario from "components/RH/inputs/EnderecoFuncionario";

import MaskUtil from "utils/MaskUtil";
import { useNotification } from "context/NotificationContext";

const DadosPessoais = ({ dados, setDados, HandleChange, BoxBtns, isNewFuncionario, onNextTab, onSave, onSubmit }) => {
  const [errors, setErrors] = useState({});
  const notify = useNotification();

  const validarCampos = () => {
    const novosErrors = {};

    // Validar Nome
    if (!dados.nome || dados.nome.trim() === "") {
      novosErrors.nome = "Nome é obrigatório";
    }

    // Validar CPF
    if (!dados.cpf || dados.cpf.trim() === "") {
      novosErrors.cpf = "CPF é obrigatório";
    } else if (dados.cpf.replace(/\D/g, '').length !== 11) {
      novosErrors.cpf = "CPF deve ter 11 dígitos";
    }

    // Validar RG
    if (!dados.rg || dados.rg.trim() === "") {
      novosErrors.rg = "RG é obrigatório";
    } else if (dados.rg.replace(/\D/g, '').length < 8) {
      novosErrors.rg = "RG deve ter pelo menos 8 dígitos";
    }

    // Validar Data de Nascimento
    // Verificar se é vazio, nulo, undefined, ou máscara vazia
    if (!dados.dataNascimento || 
        dados.dataNascimento === null || 
        dados.dataNascimento === undefined || 
        (typeof dados.dataNascimento === 'string' && dados.dataNascimento.trim() === "") ||
        dados.dataNascimento === "__/__/____") { // Máscara vazia
      novosErrors.dataNascimento = "Data de Nascimento é obrigatória";
    }

    // Validar campos de endereço
    if (!dados.logradouro || dados.logradouro.trim() === "") {
      novosErrors.logradouro = "Logradouro é obrigatório";
    }
    if (!dados.numero || dados.numero.trim() === "") {
      novosErrors.numero = "Número é obrigatório";
    }
    if (!dados.bairro || dados.bairro.trim() === "") {
      novosErrors.bairro = "Bairro é obrigatório";
    }
    if (!dados.cidade || dados.cidade.trim() === "") {
      novosErrors.cidade = "Cidade é obrigatória";
    }
    if (!dados.estado || dados.estado.trim() === "") {
      novosErrors.estado = "Estado é obrigatório";
    }
    if (!dados.cep || dados.cep.trim() === "") {
      novosErrors.cep = "CEP é obrigatório";
    }

    // Validar Email
    if (!dados.email || dados.email.trim() === "") {
      novosErrors.email = "Email é obrigatório";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(dados.email)) {
      novosErrors.email = "Email deve ter um formato válido";
    }

    // Validar Telefone
    if (!dados.telefone || dados.telefone.trim() === "") {
      novosErrors.telefone = "Telefone é obrigatório";
    }

    // Validar PIS
    if (!dados.pis || dados.pis.trim() === "") {
      novosErrors.pis = "PIS é obrigatório";
    } else if (dados.pis.replace(/\D/g, '').length !== 11) {
      novosErrors.pis = "PIS deve ter 11 dígitos";
    }

    // Validar Carteira de Trabalho
    if (!dados.carteiraTrabalho || dados.carteiraTrabalho.trim() === "") {
      novosErrors.carteiraTrabalho = "Carteira de Trabalho é obrigatória";
    } else if (dados.carteiraTrabalho.replace(/\D/g, '').length > 11) {
      novosErrors.carteiraTrabalho = "Carteira de Trabalho não pode ultrapassar 11 dígitos";
    }

    // Validar Título de Eleitor
    if (!dados.tituloEleitor || dados.tituloEleitor.trim() === "") {
      novosErrors.tituloEleitor = "Título de Eleitor é obrigatório";
    } else if (dados.tituloEleitor.replace(/\D/g, '').length !== 12) {
      novosErrors.tituloEleitor = "Título de Eleitor deve ter 12 dígitos";
    }

    setErrors(novosErrors);
    return Object.keys(novosErrors).length === 0;
  };

  const handleFieldChange = (field, value) => {
    // Limpar erro do campo quando o usuário começar a digitar
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleNext = () => {
    if (validarCampos()) {
      onNextTab();
    } else {
      notify({
        type: "error",
        message: "Por favor, preencha todos os campos obrigatórios."
      });
    }
  };

  const handleSave = (e) => {
    e.preventDefault();
    if (validarCampos()) {
      // Usar o BoxBtns original que já tem a lógica de HandleSubmit
      if (onSubmit) {
        onSubmit(e);
      }
    }
  };

  const CustomBoxBtns = () => (
    <div className="d-flex justify-content-end">
      {isNewFuncionario ? (
        <Button
          type="button"
          label="Próximo"
          className="btn btn-primary"
          onClick={handleNext}
        />
      ) : (
        <Button
          type="button"
          label="Atualizar"
          className="btn btn-primary"
          onClick={handleSave}
        />
      )}
    </div>
  );

  return (
    <div className="row g-3">
      <div className="col-md-3">
        <label htmlFor="nome" className="form-label">
          Nome <span className="text-danger">*</span>
        </label>
        <InputText
          id="nome"
          name="nome"
          value={dados.nome}
          onChange={(e) => {
            HandleChange(e);
            handleFieldChange('nome', e.target.value);
          }}
          className={`form-control ${errors.nome ? 'p-invalid' : ''}`}
        />
        {errors.nome && (
          <small className="text-danger">{errors.nome}</small>
        )}
      </div>
      <div className="col-md-3">
        <label htmlFor="cpf" className="form-label">
          CPF <span className="text-danger">*</span>
        </label>
        <InputText
          id="cpf"
          name="cpf"
          value={MaskUtil.applyCpfMask(dados.cpf || "")} // Aplica a máscara ao exibir o valor
          onChange={(e) => {
            const maskedValue = MaskUtil.applyCpfMask(e.target.value);
            setDados({
              ...dados,
              cpf: maskedValue // Aplica a máscara ao alterar o valor
            });
            handleFieldChange('cpf', maskedValue);
          }}
          className={`form-control ${errors.cpf ? 'p-invalid' : ''}`}
        />
        {errors.cpf && (
          <small className="text-danger">{errors.cpf}</small>
        )}
      </div>
      <div className="col-md-3">
        <label htmlFor="rg" className="form-label">
          RG <span className="text-danger">*</span>
        </label>
        <InputText
          id="rg"
          name="rg"
          value={MaskUtil.applyRgMask(dados.rg || "")}
          onChange={(e) => {
            const maskedValue = MaskUtil.applyRgMask(e.target.value);
            setDados({
              ...dados,
              rg: maskedValue
            });
            handleFieldChange('rg', maskedValue);
          }}
          className={`form-control ${errors.rg ? 'p-invalid' : ''}`}
        />
        {errors.rg && (
          <small className="text-danger">{errors.rg}</small>
        )}
      </div>
      <div className="col-md-3">
        <label htmlFor="cnh" className="form-label">
          CNH
        </label>
        <InputText
          id="cnh"
          name="cnh"
          value={dados.cnh}
          onChange={(e) =>
            setDados({
              ...dados,
              cnh: MaskUtil.applyCnhMask(e.target.value)
            })
          }
          className="form-control"
        />
      </div>
      <div className="col-md-4">
        <label htmlFor="dataNascimento" className="form-label">
          Data de Nascimento <span className="text-danger">*</span>
        </label>
        <div 
          className={errors.dataNascimento ? 'has-error' : ''}
          style={errors.dataNascimento ? { 
            border: '1px solid #dc3545',
            borderRadius: '4px',
            backgroundColor: '#fff5f5'
          } : {}}
        >
          <InputDate
            id="dataNascimento"
            name="dataNascimento"
            value={dados.dataNascimento}
            onChange={(dateString) => {
              // Se a string estiver vazia ou for a máscara vazia, definir como string vazia
              const cleanedDate = (!dateString || dateString === "__/__/____") ? "" : dateString;
              setDados((prev) => ({ ...prev, dataNascimento: cleanedDate }));
              handleFieldChange('dataNascimento', cleanedDate);
            }}
            placeholder="dd/mm/aaaa"
            className={`form-control w-100 ${errors.dataNascimento ? 'p-invalid is-invalid border-danger' : ''}`}
            style={errors.dataNascimento ? { 
              border: '1px solid #dc3545 !important',
              boxShadow: '0 0 0 0.2rem rgba(220, 53, 69, 0.25)'
            } : {}}
          />
        </div>
        {errors.dataNascimento && (
          <small className="text-danger">{errors.dataNascimento}</small>
        )}
      </div>
      <div className="col-md-6">
        <label htmlFor="email" className="form-label">
          Email <span className="text-danger">*</span>
        </label>
        <InputText
          id="email"
          name="email"
          value={dados.email}
          onChange={(e) => {
            HandleChange(e);
            handleFieldChange('email', e.target.value);
          }}
          className={`form-control ${errors.email ? 'p-invalid' : ''}`}
        />
        {errors.email && (
          <small className="text-danger">{errors.email}</small>
        )}
      </div>
      <div className="col-md-6">
        <label htmlFor="telefone" className="form-label">
          Telefone <span className="text-danger">*</span>
        </label>
        <InputText
          id="telefone"
          name="telefone"
          value={dados.telefone}
          onChange={(e) => {
            const maskedValue = MaskUtil.applyTelefoneMask(e.target.value);
            setDados({
              ...dados,
              telefone: maskedValue
            });
            handleFieldChange('telefone', maskedValue);
          }}
          className={`form-control ${errors.telefone ? 'p-invalid' : ''}`}
        />
        {errors.telefone && (
          <small className="text-danger">{errors.telefone}</small>
        )}
      </div>
      <div className="col-md-4">
        <label htmlFor="pis" className="form-label">
          PIS <span className="text-danger">*</span>
        </label>
        <InputText
          id="pis"
          name="pis"
          value={MaskUtil.applyPisMask(dados.pis || "")}
          onChange={(e) => {
            const maskedValue = MaskUtil.applyPisMask(e.target.value);
            setDados({
              ...dados,
              pis: maskedValue
            });
            handleFieldChange('pis', maskedValue);
          }}
          className={`form-control ${errors.pis ? 'p-invalid' : ''}`}
        />
        {errors.pis && (
          <small className="text-danger">{errors.pis}</small>
        )}
      </div>
      <div className="col-md-4">
        <label htmlFor="carteiraTrabalho" className="form-label">
          Carteira de Trabalho <span className="text-danger">*</span>
        </label>
        <InputText
          id="carteiraTrabalho"
          name="carteiraTrabalho"
          value={dados.carteiraTrabalho}
          onChange={(e) => {
            setDados({
              ...dados,
              carteiraTrabalho: e.target.value
            });
            handleFieldChange('carteiraTrabalho', e.target.value);
          }}
          maxLength={11}
          className={`form-control ${errors.carteiraTrabalho ? 'p-invalid' : ''}`}
        />
        {errors.carteiraTrabalho && (
          <small className="text-danger">{errors.carteiraTrabalho}</small>
        )}
      </div>
      <div className="col-md-4">
        <label htmlFor="tituloEleitor" className="form-label">
          Título de Eleitor <span className="text-danger">*</span>
        </label>
        <InputText
          id="tituloEleitor"
          name="tituloEleitor"
          value={MaskUtil.applyTituloEleitorMask(dados.tituloEleitor || "")}
          onChange={(e) => {
            const maskedValue = MaskUtil.applyTituloEleitorMask(e.target.value);
            setDados({
              ...dados,
              tituloEleitor: maskedValue
            });
            handleFieldChange('tituloEleitor', maskedValue);
          }}
          className={`form-control ${errors.tituloEleitor ? 'p-invalid' : ''}`}
        />
        {errors.tituloEleitor && (
          <small className="text-danger">{errors.tituloEleitor}</small>
        )}
      </div>

      {/* Seção de Endereço */}
      <div className="col-12">
        <hr className="my-4" />
        <h5 className="mb-3">
          <i className="pi pi-map-marker me-2" />
          Endereço
        </h5>
        <EnderecoFuncionario
          dados={dados}
          setDados={setDados}
          errors={errors}
          obrigatorios={["cep", "logradouro", "numero", "bairro", "cidade", "estado"]}
        />
      </div>

      <div className="col-12">
        <CustomBoxBtns />
      </div>
    </div>
  );
};

DadosPessoais.propTypes = {
  dados: PropTypes.shape({
    nome: PropTypes.string,
    cpf: PropTypes.string,
    rg: PropTypes.string,
    cnh: PropTypes.string,
    dataNascimento: PropTypes.string,
    cep: PropTypes.string,
    logradouro: PropTypes.string,
    numero: PropTypes.string,
    complemento: PropTypes.string,
    bairro: PropTypes.string,
    cidade: PropTypes.string,
    estado: PropTypes.string,
    email: PropTypes.string,
    telefone: PropTypes.string,
    pis: PropTypes.string,
    carteiraTrabalho: PropTypes.string,
    tituloEleitor: PropTypes.string
  }).isRequired,
  setDados: PropTypes.func.isRequired,
  HandleChange: PropTypes.func.isRequired,
  BoxBtns: PropTypes.elementType.isRequired,
  isNewFuncionario: PropTypes.bool,
  onNextTab: PropTypes.func,
  onSave: PropTypes.func,
  onSubmit: PropTypes.func
};

DadosPessoais.defaultProps = {
  isNewFuncionario: false,
  onNextTab: () => {},
  onSave: () => {},
  onSubmit: () => {}
};

export default DadosPessoais;
